import _styled2, { css as _css, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12, default as _styled13, default as _styled14, default as _styled15, default as _styled16 } from "styled-components";
import _styled from "styled-components";
import React from "react";
import { type Options } from "plyr";
import NextHead from "next/head";
import { mergeRefs } from "react-merge-refs";
import type MuxPlayerComponent from "@mux/mux-player-react";
import type MuxPlayerElement from "@mux/mux-player";
import { type Ratio, parseRatio } from "~/features/ui/aspect-ratio";
import { VideoControls } from "~/features/ui/video/video-controls";
import { useIubendaPreferences } from "~/features/app/scripts/iubenda";
import { Icon } from "~/features/ui/icon";
import Loading from "~/public/icons/loading.svg";
import Play from "~/public/icons/play.svg";
import { useMuxPlayerA11yPatch, type MuxCSSProperties } from "./utils";
import { useVideo } from "./video-context";
import { usePageLoaded } from "./use-page-loaded";
import { useInView } from "./use-in-view";

// `next/dynamic` wraps the loaded component with `useImperativeHandle` that
// exposes `{ retry }` on the ref — the actual `<mux-player>` element is never
// forwarded. `React.lazy` preserves ref forwarding so play/pause/muted/loop
// work correctly on the player instance.
//
// The theme is imported sequentially inside the factory so it registers AFTER
// mux-player's own bundled `media-chrome`. Otherwise the theme's duplicate
// `<media-controller>` wins customElements.define() first and mux-player's
// instanceof upgrade check fails.
const MuxPlayerLazy = React.lazy(async () => {
  const mod = await import("@mux/mux-player-react");
  // @ts-expect-error: theme module has no .d.ts; side-effect import only.
  await import("@mux/mux-player/themes/minimal");
  return mod;
});
const MuxPlayer = React.forwardRef<MuxPlayerElement, React.ComponentProps<typeof MuxPlayerComponent>>(function MuxPlayer(props, ref) {
  const pageLoaded = usePageLoaded();
  if (!pageLoaded) return null;
  return <React.Suspense fallback={null}>
        <MuxPlayerLazy {...props} ref={ref} />
      </React.Suspense>;
}) as typeof MuxPlayerComponent;
const providers = {
  vimeo: "vimeo",
  youtube: "youtube",
  mux: "mux"
} as const;
export type Provider = keyof typeof providers;
export type Props = {
  ratio?: Ratio;
  videoId?: string;
  cover?: React.ReactNode;
  videoProvider?: Provider;
  fit?: keyof typeof videoFit;
  controls?: React.ReactNode;
  rounded?: boolean;
  options?: Omit<Options, "ratio">;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  /**
   * Render as a silent, autoplaying background video with no player JS.
   * Mux only — uses the static MP4 rendition so the video element is
   * SSR-able and starts playing without waiting for hydration.
   * The cover (if provided) renders behind the video so it can serve as
   * the LCP element while the video loads.
   */
  background?: boolean;
  /**
   * Show native player controls (play/pause, volume, seek).
   * Mux only — enables the built-in minimal theme controls instead of
   * the default chromeless autoplay mode.
   */
  showControls?: boolean;
  /**
   * Overlay a custom play/pause toggle on the chromeless autoplay video.
   * Mux only — keeps the video autoplaying and looping (unlike showControls,
   * which switches to click-to-play) while restoring the same top-right
   * toggle the inline/file player shows. Used to keep parity for videos
   * migrated from `kind:"file"` to `kind:"mux"`.
   */
  toggleControls?: boolean;
  /** Preload the Mux thumbnail as a high-priority resource. */
  priority?: boolean;
};
const coverStyle = {
  "position": "absolute",
  "inset": "0",
  "zIndex": "10",
  "display": "flex",
  "height": "100%",
  "width": "100%",
  "alignItems": "center",
  "justifyContent": "center",
  "transitionProperty": "opacity",
  "transitionDuration": "250ms",
  "transitionTimingFunction": "ease-in-out",
  "&::before": {
    "content": "var(--tw-content)",
    "position": "absolute",
    "inset": "0",
    "zIndex": "10",
    "height": "100%",
    "width": "100%",
    "backgroundColor": "rgba(0, 7, 4, 0.4)",
    "--tw-content": "\"\""
  }
};
const videoFit = {
  fill: {
    "height": "auto",
    "width": "100%",
    "maxWidth": "100%"
  },
  cover: _css`
    height: 100%;
    width: 100%;

    .plyr {
      height: 100%;
      width: 100%;
    }
  `
} as const;
const defaultOptions: Omit<Plyr.Options, "ratio"> = {
  controls: ["play", "progress", "current-time", "volume", "fullscreen"],
  muted: false,
  autoplay: false,
  hideControls: false,
  disableContextMenu: true,
  fullscreen: {
    enabled: true,
    fallback: true,
    iosNative: true
  }
};
const StyledPlyrVideo = /*#__PURE__*/_styled.div.withConfig({
  displayName: "external-video__StyledPlyrVideo",
  componentId: "sc-smtm15-0"
})(["--plyr-color-main:", ";--plyr-control-spacing:", ";--plyr-control-icon-size:", ";--plyr-video-controls-background:", ";--plyr-video-control-color-hover:", ";--plyr-video-control-background-hover:", ";--plyr-range-thumb-height:0px;--plyr-range-track-height:", ";--plyr-font-family:", ";.plyr__video-wrapper{", "}.plyr__video-embed__container{", "}.plyr__video-embed__container > iframe{", "}.plyr--full-ui input[type=\"range\"]{", "}.plyr--video .plyr__controls{", "}.plyr--full-ui input[type=\"range\"]::-webkit-slider-runnable-track{", "}.plyr--video .plyr__controls{", "}", " ", ""], {
  "light": "#FFFFFF",
  "dark": "#000704"
}, "1.6rem", "1.2rem", "rgba(0, 7, 4, 0.4)", {
  "light": "#FFFFFF",
  "dark": "#000704"
}, "rgba(0, 7, 4, 0.4)", "0.4rem", "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif", {
  "cursor": "pointer"
}, {
  "height": "100%",
  "width": "100%"
}, {
  "height": "100%",
  "width": "100%"
}, {
  "borderRadius": "0"
}, {
  "--tw-backdrop-blur": "blur(8px)",
  "backdropFilter": "var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia)"
}, {
  "borderRadius": "0"
}, {
  "padding": "0"
}, ({
  $fit
}: {
  $fit: keyof typeof videoFit;
}) => videoFit[$fit], ({
  $rounded
}) => $rounded && {
  "borderRadius": "4px"
});
var _StyledStyledPlyrVideo2 = /*#__PURE__*/_styled(StyledPlyrVideo).withConfig({
  displayName: "external-video___StyledStyledPlyrVideo2",
  componentId: "sc-smtm15-1"
})({
  "position": "relative",
  "isolation": "isolate",
  "overflow": "hidden"
});
var _StyledStyledPlyrVideo = /*#__PURE__*/_styled(StyledPlyrVideo).withConfig({
  displayName: "external-video___StyledStyledPlyrVideo",
  componentId: "sc-smtm15-2"
})({
  "position": "relative",
  "isolation": "isolate",
  "overflow": "hidden"
});
type BackgroundMuxVideoProps = {
  videoId: string;
  cover?: React.ReactNode;
  fit?: keyof typeof videoFit;
  aspectRatio?: ReturnType<typeof parseRatio>;
  rounded?: boolean;
} & Omit<React.HTMLAttributes<HTMLDivElement>, "children">;
function BackgroundMuxVideo(props: BackgroundMuxVideoProps) {
  const {
    videoId,
    cover,
    fit = "cover",
    aspectRatio,
    rounded,
    ...rest
  } = props;
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [coverVisible, setCoverVisible] = React.useState(true);
  // Keep the background path on the plain Mux master playlist. Unlike the
  // interactive Mux player below, this intentionally does not add
  // `min_resolution=1080p`, so HLS/native ABR can start lower and ramp up.
  const hlsSrc = `https://stream.mux.com/${videoId}.m3u8`;
  const pageLoaded = usePageLoaded();
  const [inViewRef, inView] = useInView();
  React.useEffect(() => {
    if (!pageLoaded || !inView) return;
    const video = videoRef.current;
    if (!video) return;
    const hideCover = () => {
      setCoverVisible(false);
      clearTimeout(fallbackTimer);
    };

    // Always reveal after 10s even if quality detection fails.
    const fallbackTimer = setTimeout(hideCover, 10_000);

    // Safari / iOS report "probably" — they decode HLS natively.
    const nativeHls = video.canPlayType("application/vnd.apple.mpegurl") === "probably";
    if (nativeHls) {
      if (video.src !== hlsSrc) video.src = hlsSrc;
      video.addEventListener("canplaythrough", () => hideCover(), {
        once: true
      });
      void video.play().catch(() => {});
      return () => {
        clearTimeout(fallbackTimer);
      };
    }

    // Other browsers (Chrome, Firefox, Edge) need hls.js. Lazy-loaded
    // so it's only fetched on pages that actually use a background
    // video — keeps it out of the initial bundle.
    let hls: {
      destroy: () => void;
    } | null = null;
    let cancelled = false;
    void import("hls.js").then(({
      default: Hls
    }) => {
      if (cancelled || !video) return;
      if (!Hls.isSupported()) return;
      const instance = new Hls({
        capLevelToPlayerSize: true,
        maxBufferLength: 10
      });
      instance.loadSource(hlsSrc);
      instance.attachMedia(video);
      let qualityThreshold = 720;
      instance.on(Hls.Events.MANIFEST_PARSED, () => {
        const maxHeight = Math.max(...instance.levels.map(l => l.height));
        if (maxHeight < 720) qualityThreshold = maxHeight;
        void video.play().catch(() => {});
      });

      // Hide cover once a fragment at an acceptable quality is buffered.
      instance.on(Hls.Events.FRAG_BUFFERED, (_event, data) => {
        const level = instance.levels[data.frag.level];
        if (level && level.height >= qualityThreshold) {
          hideCover();
        }
      });
      hls = instance;
    });
    return () => {
      cancelled = true;
      clearTimeout(fallbackTimer);
      hls?.destroy();
    };
  }, [hlsSrc, pageLoaded, inView]);
  return <_StyledDiv ref={inViewRef as React.Ref<HTMLDivElement>} {...rest} $_css2={[{
    "position": "relative",
    "isolation": "isolate",
    "width": "100%",
    "overflow": "hidden"
  }, fit === "cover" && {
    "height": "100%"
  }, fit === "fill" && _css`
            aspect-ratio: ${aspectRatio ? `${aspectRatio[0]} / ${aspectRatio[1]}` : "16 / 9"};
          `, rounded && {
    "borderRadius": "4px"
  }]}>
      <_StyledVideo ref={videoRef} autoPlay muted loop playsInline preload="metadata" tabIndex={-1} aria-hidden />
      {cover && <_StyledDiv2 $_css3={[{
      "pointerEvents": "none",
      "position": "absolute",
      "inset": "0",
      "zIndex": "10",
      "height": "100%",
      "width": "100%",
      "transitionProperty": "opacity",
      "transitionDuration": "500ms",
      "transitionTimingFunction": "ease-in-out"
    }, !coverVisible && {
      "opacity": "0"
    }]}>
          {cover}
        </_StyledDiv2>}
    </_StyledDiv>;
}
export function ExternalVideo(props: Props) {
  const {
    videoId,
    ratio = "16/9",
    fit = "fill",
    controls,
    rounded = true,
    options: userOptions,
    cover,
    videoProvider = "vimeo",
    autoPlay = false,
    muted: mutedProp = false,
    loop: loopProp = false,
    background = false,
    showControls = false,
    toggleControls = false,
    priority = false,
    ...rest
  } = props;
  const aspectRatio = parseRatio(ratio);
  const plyrRatio = aspectRatio?.join(":");
  const options = React.useMemo(() => ({
    ...defaultOptions,
    ...userOptions,
    ...(autoPlay && {
      autoplay: true,
      muted: true
    }),
    ...(mutedProp && {
      muted: true
    }),
    ...(loopProp && {
      loop: {
        active: true
      }
    }),
    ...(autoPlay && {
      controls: [] as string[],
      hideControls: true
    }),
    ratio: plyrRatio
  }), [autoPlay, loopProp, mutedProp, plyrRatio, userOptions]);

  // Create an id to initialize the plyr instance.
  // Using a ref to store a reference to the html element,
  // causes plyr to fail re-initialization after the destroy event.
  const plyrId = React.useId();
  const muxRef = React.useRef<MuxPlayerElement | null>(null);
  const [muxEl, setMuxEl] = React.useState<MuxPlayerElement | null>(null);
  const muxRefCallback = React.useCallback((el: MuxPlayerElement | null) => {
    muxRef.current = el;
    setMuxEl(el);
  }, []);
  const preferences = useIubendaPreferences();

  // The id must not contain a colon, because it is used as a selector.
  const safePlyrId = plyrId.replaceAll(":", "");
  const {
    isWaiting,
    setIsWaiting,
    setVideoRef,
    hasPlayedOnce,
    setHasPlayedOnce
  } = useVideo();
  const [hasEngaged, setHasEngaged] = React.useState(false);
  const earlyInViewOptions = React.useMemo(() => ({
    rootMargin: "500px 0px"
  }), []);
  const [earlyInViewRef, earlyInView] = useInView(earlyInViewOptions);
  const [inViewRef, inView] = useInView();
  const containerRef = React.useMemo(() => mergeRefs([earlyInViewRef, inViewRef] as React.Ref<HTMLDivElement>[]),
  // refs are stable — no deps needed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  useMuxPlayerA11yPatch(muxEl);
  React.useEffect(() => {
    if (!inView || showControls) return;
    const el = muxRef.current;
    if (el && el.paused) void el.play().catch(() => {});
  }, [inView, showControls]);
  React.useEffect(() => {
    if (videoProvider === "mux" && muxRef.current) {
      setVideoRef(muxRef);
    }
    return () => {
      setVideoRef(null);
    };
  }, [setVideoRef, videoProvider]);
  React.useEffect(() => {
    if (!preferences.measurement) {
      return;
    }
    let player: Plyr | null = null;
    let isDisposed = false;
    Promise.all([import("plyr"), import("plyr/dist/plyr.css")]).then(([mod]) => {
      if (isDisposed) {
        return;
      }
      const Plyr = mod.default ?? mod; // Ensure we get the correct class

      player = new Plyr(`#${safePlyrId}`, options);
      player.on("playing", () => setHasPlayedOnce(true));
      player.on("waiting", () => setIsWaiting(true));
    });
    return () => {
      isDisposed = true;
      player?.destroy();
    };
  }, [options, preferences.measurement, safePlyrId, setHasPlayedOnce, setIsWaiting]);
  if (!videoId || !(videoProvider in providers)) {
    return null;
  }

  // Background video: render a plain SSR <video> with Mux's HLS source
  // so the element exists before hydration and starts playing without
  // loading the mux-player bundle. Safari/iOS handles HLS natively;
  // other browsers get hls.js attached lazily on the client. The cover
  // (when provided) sits behind the video so it can be the LCP element.
  if (background && videoProvider === "mux") {
    return <BackgroundMuxVideo videoId={videoId} cover={cover} fit={fit} aspectRatio={aspectRatio} rounded={rounded} {...rest} />;
  }
  if (videoProvider === "mux") {
    const handleEngage = () => {
      const player = muxRef.current;
      if (player) {
        player.muted = false;
        if (player.paused) void player.play();
      }
      setHasEngaged(true);
    };
    const muxThumbnailUrl = priority && !cover ? `https://image.mux.com/${videoId}/thumbnail.jpg` : null;
    return <>
        {muxThumbnailUrl && <NextHead>
            <link key={`preload-mux-${videoId}`} rel="preload" as="image" href={muxThumbnailUrl} />
          </NextHead>}
        <_StyledDiv3 ref={containerRef as React.Ref<HTMLDivElement>} {...rest} $_css4={[{
        "position": "relative",
        "isolation": "isolate",
        "overflow": "hidden"
      }, fit === "cover" && _css`
                height: 100%;
                width: 100%;
              `, rounded && {
        "borderRadius": "4px"
      }]}>
          {cover && <_StyledDiv4 $_css5={[{
          "pointerEvents": "none"
        }, coverStyle, hasPlayedOnce && {
          "opacity": "0"
        }]}>{cover}</_StyledDiv4>}
          {toggleControls && !showControls && <_StyledVideoControls videoRef={muxRef} />}
          {showControls && !hasEngaged && <_StyledDiv5 onClick={handleEngage} $_css6={[{
          "position": "absolute",
          "inset": "0",
          "zIndex": "20",
          "display": "flex",
          "cursor": "pointer",
          "alignItems": "center",
          "justifyContent": "center"
        }, {
          "transitionProperty": "opacity",
          "transitionDuration": "250ms",
          "transitionTimingFunction": "ease-in-out"
        }]}>
              <VideoControls as="div" size="large" variant="secondary" icon={Play} />
            </_StyledDiv5>}
          {earlyInView && <MuxPlayer ref={muxRefCallback} title="Video" playsInline streamType="on-demand" playbackId={videoId} poster={cover ? "" : undefined}
        // Mux Player turns this into a `min_resolution=1080p` playlist
        // query. That is useful for intentional playback quality, but it
        // is not equivalent to the decorative background path above.
        minResolution="1080p" primaryColor="#FFFFFF" disableCookies={!preferences.measurement} disableTracking={!preferences.measurement} secondaryColor="#000000" defaultHiddenCaptions defaultShowRemainingTime={false} disablePictureInPicture theme="minimal" preload="metadata" autoPlay={!showControls && inView ? "muted" : false} muted={!showControls || !hasEngaged} loop={!showControls} onPlaying={() => setHasPlayedOnce(true)} onEnded={() => {
          if (showControls) return;
          // React 19 + mux-player-react fails to propagate the `loop`
          // boolean attr to the underlying custom element. Instead of
          // fighting the attribute, restart playback manually.
          const el = muxRef.current;
          if (el) {
            el.currentTime = 0;
            void el.play();
          }
        }} onError={(event: Event) => {
          event.stopPropagation();
          event.stopImmediatePropagation?.();
        }} style={fit === "cover" ? {
          "--media-object-fit": "cover",
          ...(!showControls && {
            "--controls": "none"
          }),
          display: "block",
          width: "100%",
          height: "100%",
          overflow: "hidden"
        } as MuxCSSProperties : {
          ...(!showControls && {
            "--controls": "none"
          }),
          display: "block",
          overflow: "hidden",
          ...(aspectRatio && {
            aspectRatio: `${aspectRatio[0]} / ${aspectRatio[1]}`
          })
        } as MuxCSSProperties} />}
        </_StyledDiv3>
      </>;
  }
  if (!preferences.measurement) {
    return <_StyledStyledPlyrVideo $fit={fit} $rounded={rounded} {...rest}>
        {preferences.isReady ? <_StyledDiv6>
            <_StyledP>
              This video requires measurement cookies to be enabled. Please update your cookie preferences to view this
              content.
            </_StyledP>
            <_StyledButton className="iubenda-cs-preferences-link" onClick={() => {
          //@ts-ignore
          window._iub.cs.api.openPreferences();
        }}>
              Update Cookie Preferences
            </_StyledButton>
          </_StyledDiv6> : <_StyledDiv7>
            <Icon icon={Loading} size="24" />
          </_StyledDiv7>}
      </_StyledStyledPlyrVideo>;
  }
  return <_StyledStyledPlyrVideo2 $fit={fit} $rounded={rounded} {...rest}>
      {cover && <_StyledDiv8 $_css7={[{
      "pointerEvents": "none"
    }, coverStyle, hasPlayedOnce && {
      "opacity": "0"
    }]}>
          {cover}
          {Boolean(controls) ? controls : <_StyledDiv9>
              <VideoControls as="div" size="large" variant="secondary" icon={isWaiting ? Loading : Play} />
            </_StyledDiv9>}
        </_StyledDiv8>}
      <div id={safePlyrId} data-plyr-provider={videoProvider} data-plyr-embed-id={videoId} />
    </_StyledStyledPlyrVideo2>;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  displayName: "external-video___StyledDiv",
  componentId: "sc-smtm15-3"
})(["", ""], p => p.$_css2);
var _StyledVideo = /*#__PURE__*/_styled("video").withConfig({
  displayName: "external-video___StyledVideo",
  componentId: "sc-smtm15-4"
})({
  "pointerEvents": "none",
  "position": "absolute",
  "inset": "0",
  "height": "100%",
  "width": "100%",
  "objectFit": "cover"
});
var _StyledDiv2 = /*#__PURE__*/_styled("div").withConfig({
  displayName: "external-video___StyledDiv2",
  componentId: "sc-smtm15-5"
})(["", ""], p => p.$_css3);
var _StyledDiv3 = /*#__PURE__*/_styled("div").withConfig({
  displayName: "external-video___StyledDiv3",
  componentId: "sc-smtm15-6"
})(["", ""], p => p.$_css4);
var _StyledDiv4 = /*#__PURE__*/_styled("div").withConfig({
  displayName: "external-video___StyledDiv4",
  componentId: "sc-smtm15-7"
})(["", ""], p => p.$_css5);
var _StyledVideoControls = /*#__PURE__*/_styled(VideoControls).withConfig({
  displayName: "external-video___StyledVideoControls",
  componentId: "sc-smtm15-8"
})({
  "position": "absolute",
  "right": "1.6rem",
  "top": "1.6rem",
  "zIndex": "20"
});
var _StyledDiv5 = /*#__PURE__*/_styled("div").withConfig({
  displayName: "external-video___StyledDiv5",
  componentId: "sc-smtm15-9"
})(["", ""], p => p.$_css6);
var _StyledDiv6 = /*#__PURE__*/_styled("div").withConfig({
  displayName: "external-video___StyledDiv6",
  componentId: "sc-smtm15-10"
})({
  "display": "flex",
  "height": "100%",
  "minHeight": "80svh",
  "flexDirection": "column",
  "alignItems": "center",
  "justifyContent": "center",
  "gap": "2.4rem",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(0 7 4 / var(--tw-bg-opacity))",
  "padding": "2.4rem",
  "textAlign": "center",
  "--tw-text-opacity": "1",
  "color": "rgb(255 255 255 / var(--tw-text-opacity))"
});
var _StyledP = /*#__PURE__*/_styled("p").withConfig({
  displayName: "external-video___StyledP",
  componentId: "sc-smtm15-11"
})({
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.014000000000000002rem",
    "fontWeight": "400"
  }
});
var _StyledButton = /*#__PURE__*/_styled("button").withConfig({
  displayName: "external-video___StyledButton",
  componentId: "sc-smtm15-12"
})({
  "fontSize": "1.4rem",
  "lineHeight": "1.1",
  "letterSpacing": "0.028000000000000004rem",
  "fontWeight": "400",
  "textDecorationLine": "underline",
  "transitionProperty": "opacity",
  "transitionDuration": "250ms",
  "transitionTimingFunction": "ease-in-out",
  "@media (hover: hover)": {
    "&:hover": {
      "opacity": "0.6"
    }
  },
  "@media (min-width: 1024px)": {
    "fontSize": "1.6rem",
    "lineHeight": "1.1",
    "letterSpacing": "0.016rem",
    "fontWeight": "400"
  }
});
var _StyledDiv7 = /*#__PURE__*/_styled("div").withConfig({
  displayName: "external-video___StyledDiv7",
  componentId: "sc-smtm15-13"
})({
  "display": "flex",
  "height": "100%",
  "minHeight": "80svh",
  "flexDirection": "column",
  "alignItems": "center",
  "justifyContent": "center",
  "gap": "2.4rem",
  "padding": "2.4rem",
  "textAlign": "center"
});
var _StyledDiv8 = /*#__PURE__*/_styled("div").withConfig({
  displayName: "external-video___StyledDiv8",
  componentId: "sc-smtm15-14"
})(["", ""], p => p.$_css7);
var _StyledDiv9 = /*#__PURE__*/_styled("div").withConfig({
  displayName: "external-video___StyledDiv9",
  componentId: "sc-smtm15-15"
})({
  "position": "relative",
  "zIndex": "20"
});